import { Gauge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Notify } from "@/components/ui/notify";
import { TrackRecordByLine } from "./TrackRecordByLine";
import { TrackRecordMetricRows } from "./TrackRecordMetricRows";
import { TrackRecordSummary } from "./TrackRecordSummary";
import { useTrackRecord } from "./hooks";

// Spec 079 (US1) — a aba da RÉGUA DO GÊMEO.
//
// Orquestrador da aba: `closed-loop/index.tsx` fica sendo só o roteador de abas, e todo o conteúdo
// da régua nasce aqui, ao lado.
//
// ⚠️ `isError` é checado ANTES de decidir entre vazio e com dados (FR-029). Uma régua que falhou
// desenhada como régua vazia diria "o gêmeo ainda não foi medido" quando a verdade é "não sei" — e
// o efeito colateral é pior que a tela: sem nada dizendo que houve falha, o nível de confiança
// exibido nas simulações cairia silenciosamente de volta na procedência.

interface TrackRecordTabProps {
  /**
   * Leva para a fila de apurações aguardando julgamento — a aba IRMÃ desta, na mesma página.
   *
   * Vem de fora porque quem controla a aba ativa é o orquestrador. O estado vazio manda o gestor
   * para a fila que ele acabou de ler na frase; navegar para outra tela o tiraria justamente de
   * onde o próximo passo acontece.
   */
  onGoToAwaiting: () => void;
}

export function TrackRecordTab({ onGoToAwaiting }: TrackRecordTabProps) {
  const query = useTrackRecord();

  if (query.isError) {
    return (
      <Notify.QueryError
        error={query.error}
        onRetry={() => void query.refetch()}
        title="Não foi possível carregar a régua do gêmeo"
      />
    );
  }

  if (query.isPending) {
    return <p className="text-sm text-muted-foreground">Carregando…</p>;
  }

  const { organization, lines } = query.data;
  // "Nada medido" é sampleSize zero E nenhuma linha para mostrar: uma linha cuja régua foi zerada
  // por conserto tem amostra zero e PRECISA aparecer, com o marco ao lado.
  const nothingMeasured = organization.sampleSize === 0 && lines.length === 0;

  if (nothingMeasured) {
    return (
      <Notify.Empty
        icon={<Gauge className="h-5 w-5" />}
        title="O gêmeo ainda não foi medido"
        description="A régua nasce da apuração: quando um compromisso é apurado e alguém confirma a causa da divergência, ele passa a contar aqui. Comece pela fila de apurações aguardando julgamento."
        action={
          <Button variant="outline" onClick={onGoToAwaiting}>
            Ver apurações aguardando julgamento
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <div>
          <h2 className="text-[13px] font-medium text-text-primary">A fábrica inteira</h2>
          <p className="text-[12px] text-text-secondary">
            Desde o marco zero de cada linha — o que veio antes do último conserto do modelo não
            conta, porque o gêmeo de antes do conserto não é este.
          </p>
        </div>
        <TrackRecordSummary scope={organization} />
        <TrackRecordMetricRows scope={organization} />
      </section>

      {lines.length > 0 ? (
        <section className="space-y-2">
          <h2 className="text-[13px] font-medium text-text-primary">Por linha</h2>
          <TrackRecordByLine lines={lines} />
        </section>
      ) : null}
    </div>
  );
}
